import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDateForDisplay } from '../../../../../../utils/formatters';
import {
  PATIENT_DOCUMENT_ACCEPT,
  createVacationSupplyForm,
  patientDocumentFileProblem,
  uploadPatientDocument,
  type PhoxPatientDocument,
  type VacationSupplyFormRequest,
} from '../../../../../../services/patientDocumentsService';

/** What happened to the proof of travel: none was attached, it was saved, or its upload failed after the form was saved. */
export type ProofOfTravelOutcome = 'none' | 'saved' | 'failed';

export interface VacationSupplyFormCreated {
  document: PhoxPatientDocument;
  /** True when the identical form was already saved today: no second form was made. */
  alreadyExisted: boolean;
  /** True when this is a new form and a different one for the same trip was saved earlier today. */
  earlierFormToday: boolean;
  proof: ProofOfTravelOutcome;
}

interface VacationSupplyFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** The patient's pharmacy: the form is saved there. */
  pharmacyId: string;
  /** The composite `<pharmacyId>_<patientNumber>`. */
  patientId: string;
  /** What the form prints. */
  request: VacationSupplyFormRequest;
  onCreated: (result: VacationSupplyFormCreated) => void;
}

const plural = (n: number, word: string) => `${n} ${n === 1 ? word : `${word}s`}`;
const shortNumber = (n: number) => String(Number(n.toFixed(2)));

/**
 * WP-10 (PRX-130 Q10, Q15) — at a Fillware pharmacy a vacation supply is a
 * form for the patient's signature, not a task. This dialog shows what the
 * form will say, takes an optional proof of travel (kept on file, asked for
 * when the trip is outside Ontario), and saves both to the patient's
 * documents. The form is tied to the Rx numbers it covers.
 */
export const VacationSupplyFormDialog: React.FC<VacationSupplyFormDialogProps> = ({
  open,
  onClose,
  pharmacyId,
  patientId,
  request,
  onCreated,
}) => {
  const queryClient = useQueryClient();
  const { user } = useAuth0();
  const [proof, setProof] = useState<File | null>(null);
  const [proofProblem, setProofProblem] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async (): Promise<VacationSupplyFormCreated> => {
      const saved = await createVacationSupplyForm(pharmacyId, patientId, request);
      if (!proof) return { ...saved, proof: 'none' };
      const uploadedBy = user?.name || user?.email;
      // The form is saved by now: a failed proof upload is reported, never retried into a second form.
      if (!uploadedBy) return { ...saved, proof: 'failed' };
      try {
        await uploadPatientDocument({
          patientId,
          pharmacyId,
          file: proof,
          documentType: 'proof_of_travel',
          uploadedBy,
          title: `Proof of travel: ${request.departureDate} to ${request.returnDate}`,
        });
        return { ...saved, proof: 'saved' };
      } catch {
        return { ...saved, proof: 'failed' };
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['patient-documents', patientId] });
      onCreated(result);
    },
  });
  const { reset } = create;

  // Each opening starts clean: no file, no last outcome.
  useEffect(() => {
    if (open) {
      setProof(null);
      setProofProblem(null);
      reset();
    }
  }, [open, reset]);

  const chooseProof = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    if (!file) return;
    const problem = patientDocumentFileProblem(file);
    setProofProblem(problem);
    setProof(problem ? null : file);
  };

  const serverMessage: unknown = (create.error as any)?.response?.data?.message;
  const errorMessage = !create.error
    ? null
    : Array.isArray(serverMessage)
      ? serverMessage.join('; ')
      : typeof serverMessage === 'string' && serverMessage
        ? serverMessage
        : "Couldn't create the form";
  const outsideOntario = request.destination === 'outside_ontario';

  return (
    <Dialog open={open} onClose={create.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create vacation supply form</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            This saves a form for the patient to sign to the patient's documents, tied to the prescriptions below. The
            patient's name is printed from the PHOX file. No task is created. Nothing is billed and nothing is written
            to the pharmacy system.
          </Typography>
          <Box data-testid="vacation-form-summary">
            <Typography variant="body2">
              {formatDateForDisplay(request.departureDate)} to {formatDateForDisplay(request.returnDate)},{' '}
              {outsideOntario ? 'outside Ontario' : 'in Ontario'}
            </Typography>
            <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5, typography: 'body2' }}>
              {request.items.map((item) => (
                <li key={item.rxNumber}>
                  Rx {item.rxNumber} {item.drugName}: {shortNumber(item.fillQty)} ({plural(item.fillDays, 'day')})
                  {item.bridgeQty > 0 &&
                    `, bridge fill ${shortNumber(item.bridgeQty)} (${plural(item.bridgeDays, 'day')})`}
                </li>
              ))}
            </Box>
          </Box>
          <Box>
            <Typography variant="subtitle2">Proof of travel</Typography>
            {outsideOntario && !proof ? (
              <Alert severity="warning" sx={{ mt: 1 }}>
                This trip is outside Ontario. Attach proof of travel, for example a ticket, so it is kept on file.
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Proof of travel, for example a ticket, is kept on file with a vacation supply.
              </Typography>
            )}
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap">
              <Button component="label" size="small" variant="outlined" disabled={create.isPending}>
                {proof ? 'Change file' : 'Attach proof of travel'}
                <input
                  hidden
                  type="file"
                  accept={PATIENT_DOCUMENT_ACCEPT}
                  aria-label="Proof of travel file"
                  onChange={chooseProof}
                />
              </Button>
              {proof && (
                <>
                  <Typography variant="body2" data-testid="vacation-form-proof-name">
                    {proof.name}
                  </Typography>
                  <Button size="small" onClick={() => setProof(null)} disabled={create.isPending}>
                    Remove
                  </Button>
                </>
              )}
            </Stack>
            {proofProblem && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {proofProblem}
              </Alert>
            )}
          </Box>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={create.isPending}>
          Cancel
        </Button>
        <Button variant="contained" disabled={create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? 'Creating' : 'Create form'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VacationSupplyFormDialog;
